import {
  NestedLexicalEditor,
  useMdastNodeUpdater,
  type DirectiveDescriptor,
  type DirectiveEditorProps,
} from '@mdxeditor/editor'
import {
  ANAMNESIS_EMPTY_ATTRIBUTE,
  DEFAULT_ANAMNESIS_EMPTY_TEXT,
  DIRECTIVES,
  GALLERY_EMPTY_ATTRIBUTE,
  formatIsoDate,
  getBlockTitle,
  getDirectiveDefinition,
  getGalleryColumns,
} from '@westhetics/legal'
import type { ContainerDirective, LeafDirective, TextDirective } from 'mdast-util-directive'
import { useRef, useState } from 'react'

/**
 * Como cada bloco do dialeto aparece DENTRO do editor: mesma hierarquia visual do documento
 * (título, fio plum/lilás, fundo pampas), com o conteúdo editável no lugar. O título é o padrão do
 * dialeto; clicar nele troca o rótulo (atributo `title`, T40): Enter confirma, Esc cancela, vazio
 * volta ao padrão. O que se vê editando é o que o paciente vai ler.
 */

function BlockEditor({
  mdastNode,
  lexicalNode,
  parentEditor,
}: DirectiveEditorProps<ContainerDirective>) {
  const def = getDirectiveDefinition(mdastNode.name)
  const updateNode = useMdastNodeUpdater<ContainerDirective>()
  const attributes = mdastNode.attributes ?? {}
  const date = String(attributes.date ?? '')
  const customTitle = String(attributes.title ?? '').trim()
  const defaultTitle = def?.title ?? mdastNode.name
  const title = getBlockTitle(mdastNode.name, attributes) ?? mdastNode.name
  const [editingDate, setEditingDate] = useState(false)
  const [editingTitle, setEditingTitle] = useState(false)
  // Esc marca o cancelamento; o blur que vem em seguida não grava.
  const cancelled = useRef(false)

  const remove = () => parentEditor.update(() => lexicalNode.remove())
  const setAttribute = (key: string, value: string) => {
    const next = { ...attributes }
    if (value) next[key] = value
    else delete next[key]
    updateNode({ attributes: next })
  }
  const commitTitle = (value: string) => {
    setEditingTitle(false)
    if (cancelled.current) {
      cancelled.current = false
      return
    }
    const trimmed = value.trim()
    setAttribute('title', trimmed === defaultTitle ? '' : trimmed)
  }

  return (
    <section
      className={`wx-ed-block wx-ed-block--${mdastNode.name}`}
      data-block={mdastNode.name}
      contentEditable={false}
    >
      <div className="wx-ed-block-header">
        <span className="wx-ed-block-heading">
          {editingTitle ? (
            <input
              type="text"
              className="wx-ed-input wx-ed-block-title-input"
              defaultValue={customTitle}
              placeholder={defaultTitle}
              aria-label={`Título do bloco ${defaultTitle}`}
              autoFocus
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  event.currentTarget.blur()
                } else if (event.key === 'Escape') {
                  event.preventDefault()
                  cancelled.current = true
                  event.currentTarget.blur()
                }
              }}
              onBlur={(event) => commitTitle(event.target.value)}
            />
          ) : (
            <button
              type="button"
              className="wx-ed-block-title"
              title="Mudar o título do bloco"
              aria-label={`Mudar o título do bloco ${title}`}
              onClick={() => setEditingTitle(true)}
            >
              {title}
            </button>
          )}
          {mdastNode.name === 'update' && date ? (
            <span className="wx-ed-block-date"> · {formatIsoDate(date)}</span>
          ) : null}
        </span>
        <span className="wx-ed-block-actions">
          {mdastNode.name === 'update' ? (
            editingDate ? (
              <input
                type="date"
                className="wx-ed-input"
                defaultValue={date}
                autoFocus
                onBlur={(event) => {
                  setAttribute('date', event.target.value)
                  setEditingDate(false)
                }}
                aria-label="Data da atualização"
              />
            ) : (
              <button type="button" onClick={() => setEditingDate(true)}>
                {date ? 'mudar data' : 'definir data'}
              </button>
            )
          ) : null}
          <button type="button" onClick={remove} aria-label={`Remover bloco ${title}`}>
            remover
          </button>
        </span>
      </div>
      <div className="wx-ed-block-content">
        <NestedLexicalEditor<ContainerDirective>
          block
          getContent={(node) => node.children}
          getUpdatedMdastNode={(node, children) => ({ ...node, children }) as ContainerDirective}
          contentEditableProps={{ className: 'wx-ed-block-editable' }}
        />
      </div>
      {def?.description ? <div className="wx-ed-block-hint">{def.description}</div> : null}
    </section>
  )
}

/** O rótulo da galeria no editor: o dialeto não imprime título padrão, só o do atributo `title`. */
const GALLERY_LABEL = 'Galeria de fotos'

type Block = ContainerDirective['children'][number]
type Phrasing = Extract<Block, { type: 'paragraph' }>['children'][number]
type ImageNode = Extract<Phrasing, { type: 'image' }>

/** O texto de uma legenda. A hora ("14:20") chega como diretiva `:20` e volta a ser texto. */
function captionText(nodes: Phrasing[]): string {
  return nodes
    .map((node) => {
      if (node.type === 'text') return node.value
      if (node.type === 'textDirective') return `:${node.name}${captionText(node.children)}`
      return 'children' in node ? captionText(node.children as Phrasing[]) : ''
    })
    .join('')
    .trim()
}

interface GalleryFigure {
  src: string
  alt: string
  detail: string
}

/** As figuras da galeria, com a mesma leitura do renderizador: imagem e o parágrafo curto abaixo. */
function galleryFigures(children: Block[]): GalleryFigure[] {
  const figures: GalleryFigure[] = []
  for (let i = 0; i < children.length; i += 1) {
    const node = children[i]!
    if (node.type !== 'paragraph') continue
    const images = node.children.filter((c): c is ImageNode => c.type === 'image')
    if (images.length === 0) continue
    let detail = captionText(node.children.filter((c) => c.type !== 'image'))
    if (images.length === 1 && !detail) {
      const next = children[i + 1]
      if (next?.type === 'paragraph' && !next.children.some((c) => c.type === 'image')) {
        detail = captionText(next.children)
        i += 1
      }
    }
    for (const image of images) {
      figures.push({
        src: image.url,
        alt: image.alt ?? '',
        detail: images.length === 1 ? detail : '',
      })
    }
  }
  return figures
}

/**
 * A galeria de fotos (P-PLA-11) dentro do editor: só mostra. A API monta o bloco a partir do plano
 * de tratamento, com as fotos em data URI; aqui ele é preservado como veio (o nó mdast não é
 * aberto para edição) e desenhado como grade, para quem lê o Markdown saber o que está ali.
 */
function GalleryEditor({
  mdastNode,
  lexicalNode,
  parentEditor,
}: DirectiveEditorProps<ContainerDirective>) {
  const def = getDirectiveDefinition(mdastNode.name)
  const attributes = mdastNode.attributes ?? {}
  const title = getBlockTitle(mdastNode.name, attributes)
  const columns = getGalleryColumns(attributes)
  const empty = String(attributes[GALLERY_EMPTY_ATTRIBUTE] ?? '').trim()
  const figures = galleryFigures(mdastNode.children)
  const label = title ?? GALLERY_LABEL
  return (
    <section
      className="wx-ed-block wx-ed-block--gallery"
      data-block={mdastNode.name}
      contentEditable={false}
    >
      <div className="wx-ed-block-header">
        <span className="wx-ed-block-heading">
          <span className="wx-ed-block-title">{label}</span>
          {title ? <span className="wx-ed-block-date"> · {GALLERY_LABEL}</span> : null}
        </span>
        <span className="wx-ed-block-actions">
          <button
            type="button"
            onClick={() => parentEditor.update(() => lexicalNode.remove())}
            aria-label={`Remover bloco ${label}`}
          >
            remover
          </button>
        </span>
      </div>
      <div className="wx-ed-block-content">
        {figures.length ? (
          <div className={`wx-ed-gallery wx-ed-gallery--${columns}`} data-columns={columns}>
            {figures.map((figure, index) => (
              <figure key={index} className="wx-ed-gallery-item">
                <img src={figure.src} alt={figure.alt} />
                <figcaption>
                  {figure.alt}
                  {figure.detail ? <span>{figure.detail}</span> : null}
                </figcaption>
              </figure>
            ))}
          </div>
        ) : (
          <p className="wx-ed-gallery-empty">{empty || 'Sem foto nesta galeria'}</p>
        )}
      </div>
      {def?.description ? <div className="wx-ed-block-hint">{def.description}</div> : null}
    </section>
  )
}

/** O que o editor escreve no lugar das respostas: a API as monta na geração (P-DOC-03). */
const ANAMNESIS_PLACEHOLDER = 'Perguntas e respostas da anamnese entram aqui na geração'

/**
 * O bloco de anamnese (P-DOC-03) dentro do editor: só mostra. Como a galeria, a API monta o conteúdo
 * na geração; aqui o nó é preservado como veio (`title` e `empty`) e desenhado com o título e o
 * aviso de que as respostas entram depois, para quem lê o Markdown saber o que está ali.
 */
function AnamnesisEditor({
  mdastNode,
  lexicalNode,
  parentEditor,
}: DirectiveEditorProps<LeafDirective>) {
  const def = getDirectiveDefinition(mdastNode.name)
  const attributes = mdastNode.attributes ?? {}
  const title = getBlockTitle(mdastNode.name, attributes) ?? def?.title ?? mdastNode.name
  const empty =
    String(attributes[ANAMNESIS_EMPTY_ATTRIBUTE] ?? '').trim() || DEFAULT_ANAMNESIS_EMPTY_TEXT
  return (
    <section
      className="wx-ed-block wx-ed-block--anamnesis"
      data-block={mdastNode.name}
      contentEditable={false}
    >
      <div className="wx-ed-block-header">
        <span className="wx-ed-block-heading">
          <span className="wx-ed-block-title">{title}</span>
        </span>
        <span className="wx-ed-block-actions">
          <button
            type="button"
            onClick={() => parentEditor.update(() => lexicalNode.remove())}
            aria-label={`Remover bloco ${title}`}
          >
            remover
          </button>
        </span>
      </div>
      <div className="wx-ed-block-content">
        <p className="wx-ed-anamnesis-placeholder">{ANAMNESIS_PLACEHOLDER}</p>
        <p className="wx-ed-anamnesis-empty">Sem anamnese respondida, imprime: “{empty}”</p>
      </div>
      {def?.description ? <div className="wx-ed-block-hint">{def.description}</div> : null}
    </section>
  )
}

function PageBreakEditor({ lexicalNode, parentEditor }: DirectiveEditorProps<LeafDirective>) {
  return (
    <div className="wx-ed-page-break" contentEditable={false}>
      <span>quebra de página no PDF</span>
      <button type="button" onClick={() => parentEditor.update(() => lexicalNode.remove())}>
        remover
      </button>
    </div>
  )
}

function TermEditor({ mdastNode, lexicalNode, parentEditor }: DirectiveEditorProps<TextDirective>) {
  const def = String(mdastNode.attributes?.def ?? '')
  const [open, setOpen] = useState(!def)
  const setDef = (value: string) =>
    parentEditor.update(() => {
      lexicalNode.setMdastNode({
        ...mdastNode,
        attributes: { ...(mdastNode.attributes ?? {}), def: value },
      })
    })
  return (
    <span className="wx-ed-term" title={def}>
      <NestedLexicalEditor<TextDirective>
        getContent={(node) => node.children}
        getUpdatedMdastNode={(node, children) => ({ ...node, children }) as TextDirective}
        contentEditableProps={{ className: 'wx-ed-term-text' }}
      />
      <button
        type="button"
        className="wx-ed-term-button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Editar definição do termo"
        contentEditable={false}
      >
        def
      </button>
      {open ? (
        <input
          className="wx-ed-input wx-ed-term-def"
          defaultValue={def}
          placeholder="Definição em linguagem clara"
          aria-label="Definição do termo"
          onBlur={(e) => {
            setDef(e.target.value)
            setOpen(false)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
          }}
        />
      ) : null}
    </span>
  )
}

/**
 * Os descritores do MDXEditor, um por diretiva do dialeto. O sublinhado (`:u`) fica de fora: ele é
 * formato de texto no editor, não um nó próprio (ver `underline.ts`).
 */
export const directiveDescriptors: DirectiveDescriptor[] = DIRECTIVES.filter(
  (d) => d.name !== 'u',
).map((d) => {
  if (d.type === 'leafDirective') {
    return {
      name: d.name,
      type: 'leafDirective',
      attributes: d.attributes ?? [],
      hasChildren: false,
      testNode: (node) => node.type === 'leafDirective' && node.name === d.name,
      // A anamnese só é mostrada: a API monta as respostas na geração (P-DOC-03).
      Editor: (d.name === 'anamnesis'
        ? AnamnesisEditor
        : PageBreakEditor) as DirectiveDescriptor['Editor'],
    }
  }
  if (d.type === 'textDirective') {
    return {
      name: d.name,
      type: 'textDirective',
      attributes: d.attributes ?? [],
      hasChildren: true,
      testNode: (node) => node.type === 'textDirective' && node.name === d.name,
      Editor: TermEditor as DirectiveDescriptor['Editor'],
    }
  }
  return {
    name: d.name,
    type: 'containerDirective',
    attributes: d.attributes ?? [],
    hasChildren: true,
    testNode: (node) => node.type === 'containerDirective' && node.name === d.name,
    // A galeria só é mostrada: o conteúdo (fotos em data URI) não abre para edição.
    Editor: (d.name === 'gallery' ? GalleryEditor : BlockEditor) as DirectiveDescriptor['Editor'],
  }
})
